import { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Manages a single land-listing GeoJSON layer: load, toggle, and abort on re-fetch.
 * Returns { load } for use in the moveend handler.
 */
export function useListingLayer(
  map: React.RefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  sourceId: string,
  fetchFn: (bbox: string, signal: AbortSignal) => Promise<GeoJSON.FeatureCollection>,
  getBbox: () => string,
  show: boolean,
  onLoadingChange?: (loading: boolean) => void,
) {
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!map.current || !mapLoaded || !show) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    onLoadingChange?.(true);

    try {
      const data = await fetchFn(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error(`Failed to load ${sourceId}:`, err);
    } finally {
      if (abortRef.current === controller) {
        onLoadingChange?.(false);
      }
    }
  }, [map, mapLoaded, show, sourceId, fetchFn, getBbox, onLoadingChange]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (show) {
      load();
    } else {
      const src = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => abortRef.current?.abort();
  }, [show, mapLoaded, load, map, sourceId]);

  return { load };
}
